import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Printer, Download, Save, ArrowLeft, ChevronDown, Search } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { api } from '@/shared/api/axios';
import { UnitPackCell } from '@/features/catalog/components/UnitPackCell';
import { abbreviatePackaging, derivePackagingType } from '@/shared/utils/packagingAbbreviation';
import { piecesPerPack, baseUnitDivisor } from '@/shared/utils/ratePerBaseUnit';
import { useSalesAssignees } from '@/shared/hooks/useSalesAssignees';
import { DEFAULT_FALLBACK_TERMS } from '@/features/quotations/pages/QuotationFormPage';
import { RBM_LOGO_BASE64 } from '../constants/rbmLogo';

// ─────────────────────────────────────────────────────────────────────────────
// Catalogue product shape returned by /api/catalog/products
// ─────────────────────────────────────────────────────────────────────────────
interface CatalogProduct {
  _id: string;
  name: string;
  sku?: string;
  hsnCode?: string;
  // The real list endpoint (/api/catalog/products) returns the field as
  // `hsnSac`, not `hsnCode` — see useQuotationForm.ts's CatalogProduct. Only
  // the static RBM_STATIC_PRODUCTS fallback above uses `hsnCode`, so both are
  // read below rather than only the one this interface used to declare.
  hsnSac?: string;
  sellingPrice?: number;
  mrp?: number;
  unitId?: { name?: string; shortName?: string };
  taxProfileId?: { cgstRate?: number; sgstRate?: number };
  packagingType?: string;
  packSize?: string;
  qtyPerPack?: number;
  totalPackSize?: string;
  volumeLiters?: number;
  // Populated by GET /api/catalog/products (src/api-catalog/products.js) —
  // used only to build the "[Category] Name — Size x count (Packing)" label
  // in the catalogue search bar, same style as the reference template.
  divisionId?: { name?: string };
  categoryId?: { name?: string };
}

// ─────────────────────────────────────────────────────────────────────────────
// Static RBM lubricants catalogue — used as fallback when DB is empty or API
// is unreachable. Covers the 36 core RBM product families.
// ─────────────────────────────────────────────────────────────────────────────
const RBM_STATIC_PRODUCTS: CatalogProduct[] = [
  { _id: 's1',  name: 'RBM CUTTING OIL ROBUST 8500 LOOSE 8500',   hsnCode: '27101980', sellingPrice: 158,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's2',  name: 'RBM CUTTING OIL ROBUST 2000',               hsnCode: '27101980', sellingPrice: 145,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's3',  name: 'RBM CUTTING OIL ROBUST 5000',               hsnCode: '27101980', sellingPrice: 152,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's4',  name: 'RBM HYDRAULIC OIL HLP 32',                  hsnCode: '27101980', sellingPrice: 165,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's5',  name: 'RBM HYDRAULIC OIL HLP 46',                  hsnCode: '27101980', sellingPrice: 168,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's6',  name: 'RBM HYDRAULIC OIL HLP 68',                  hsnCode: '27101980', sellingPrice: 172,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's7',  name: 'RBM GEAR OIL EP 90',                        hsnCode: '27101980', sellingPrice: 170,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's8',  name: 'RBM GEAR OIL EP 140',                       hsnCode: '27101980', sellingPrice: 175,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's9',  name: 'RBM GEAR OIL EP 220',                       hsnCode: '27101980', sellingPrice: 178,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's10', name: 'RBM SPINDLE OIL 10',                        hsnCode: '27101980', sellingPrice: 195,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's11', name: 'RBM SPINDLE OIL 22',                        hsnCode: '27101980', sellingPrice: 192,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's12', name: 'RBM SLIDEWAY OIL 68',                       hsnCode: '27101980', sellingPrice: 185,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's13', name: 'RBM SLIDEWAY OIL 220',                      hsnCode: '27101980', sellingPrice: 188,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's14', name: 'RBM TURBINE OIL 32',                        hsnCode: '27101980', sellingPrice: 180,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's15', name: 'RBM TURBINE OIL 46',                        hsnCode: '27101980', sellingPrice: 182,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's16', name: 'RBM COMPRESSOR OIL 100',                    hsnCode: '27101980', sellingPrice: 205,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's17', name: 'RBM COMPRESSOR OIL 150',                    hsnCode: '27101980', sellingPrice: 210,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's18', name: 'RBM TRANSFORMER OIL',                       hsnCode: '27101980', sellingPrice: 210,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's19', name: 'RBM CIRCULATION OIL 100',                   hsnCode: '27101980', sellingPrice: 175,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's20', name: 'RBM CIRCULATION OIL 150',                   hsnCode: '27101980', sellingPrice: 178,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's21', name: 'RBM CIRCULATION OIL 220',                   hsnCode: '27101980', sellingPrice: 182,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's22', name: 'RBM QUENCHING OIL',                         hsnCode: '27101980', sellingPrice: 190,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's23', name: 'RBM HEAT TREATMENT OIL 32',                 hsnCode: '27101980', sellingPrice: 195,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's24', name: 'RBM RUST PREVENTIVE OIL RP 40',             hsnCode: '27101940', sellingPrice: 220,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's25', name: 'RBM RUST PREVENTIVE OIL RP 60',             hsnCode: '27101940', sellingPrice: 225,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's26', name: 'RBM LITHIUM GREASE EP 1',                   hsnCode: '27101980', sellingPrice: 135,  unitId: { shortName: 'KG'  }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's27', name: 'RBM LITHIUM GREASE EP 2',                   hsnCode: '27101980', sellingPrice: 138,  unitId: { shortName: 'KG'  }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's28', name: 'RBM LITHIUM GREASE EP 3',                   hsnCode: '27101980', sellingPrice: 140,  unitId: { shortName: 'KG'  }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's29', name: 'RBM COOGENT ENGINE OIL 15W40',              hsnCode: '27101980', sellingPrice: 245,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's30', name: 'RBM COOGENT ENGINE OIL 20W50',              hsnCode: '27101980', sellingPrice: 248,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's31', name: 'RBM EPR 90 GEAR OIL 210',                   hsnCode: '27101980', sellingPrice: 170,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's32', name: 'RBM BASE OIL N 500',                        hsnCode: '27101980', sellingPrice: 95,   unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's33', name: 'MORRISON FROSTGUARD COOLANT 50%',            hsnCode: '38200090', sellingPrice: 148,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's34', name: 'RBM CIRCU 525 CIRCULATION OIL',             hsnCode: '27101980', sellingPrice: 185,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's35', name: 'CPI 6800 CP COMPRESSOR OIL',                hsnCode: '27101980', sellingPrice: 420,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
  { _id: 's36', name: 'RBM SLIDEWAY OIL 32',                       hsnCode: '27101980', sellingPrice: 182,  unitId: { shortName: 'LTR' }, taxProfileId: { cgstRate: 9, sgstRate: 9 } },
];

// ─────────────────────────────────────────────────────────────────────────────
// RBM Document Editor — Exact Layout with Official RBM Logo & Live Calculations
// ─────────────────────────────────────────────────────────────────────────────

interface LineItem {
  sr: number;
  hsn: string;
  product: string;
  productId?: string;
  rate: number;
  qty: number;
  unit: string;
  /** Discount value, either percentage or a direct rupee amount. */
  discount?: number;
  discountType?: 'percent' | 'fixed';
  /** The pack's own fill size, e.g. "900 ML", "210 LTR" — same field name as salesOrderLineSchema (src/db-sales.js). */
  packSize?: string;
  /** How many fill-units make up one pack/case — same field name as salesOrderLineSchema. */
  qtyPerPack?: number;
}

// The RBM_STATIC_PRODUCTS fallback uses synthetic ids ('s1'..'s36') since
// they're never persisted to Mongo — only forward a productId that is a
// real ObjectId, so a fallback selection never gets saved as a bogus ref.
const isRealObjectId = (id: string | undefined): id is string => !!id && /^[0-9a-f]{24}$/i.test(id);

interface ClientInfo {
  name: string;
  phone: string;
  email: string;
  address: string;
  gstin: string;
  reference: string;
}

interface DocMeta {
  number: string;
  date: string;
  quotationNo: string;
  quotationDate: string;
  purchaseOrder: string;
  poDate: string;
  validUntil: string;
}

const defaultLine = (sr = 1): LineItem => ({
  sr,
  hsn: '27101980',
  product: 'RBM CUTTING OIL ROBUST 8500 LOOSE 8500',
  rate: 158.0,
  qty: 1470,
  unit: 'LTR',
  discount: 0,
  discountType: 'percent',
});

const todayStr = () => {
  const d = new Date();
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

function fmtWestern(n: number): string {
  if (isNaN(n) || n === null || n === undefined) return '0.00';
  const parts = Number(n).toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}

function numberToWordsIndian(num: number): string {
  num = Math.round(num);
  if (num === 0) return 'Zero';
  const ones = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
  ];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  function twoDigit(n: number) {
    if (n < 20) return ones[n];
    return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
  }
  function threeDigit(n: number) {
    let s = '';
    if (n >= 100) {
      s += ones[Math.floor(n / 100)] + ' Hundred';
      n = n % 100;
      if (n) s += ' ';
    }
    if (n) s += twoDigit(n);
    return s;
  }
  const crore = Math.floor(num / 10000000);
  num %= 10000000;
  const lakh = Math.floor(num / 100000);
  num %= 100000;
  const thousand = Math.floor(num / 1000);
  num %= 1000;
  const rest = num;
  const parts: string[] = [];
  if (crore) parts.push(threeDigit(crore) + ' Crore');
  if (lakh) parts.push(threeDigit(lakh) + ' Lakh');
  if (thousand) parts.push(threeDigit(thousand) + ' Thousand');
  if (rest) {
    if (parts.length) parts.push('and ' + threeDigit(rest));
    else parts.push(threeDigit(rest));
  }
  return parts.join(' ');
}

export function RbmDocumentEditorPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const docType = (searchParams.get('type') || 'invoice') as 'invoice' | 'proforma' | 'quotation';
  const docTitle = docType === 'invoice' ? 'Invoice' : docType === 'proforma' ? 'Proforma Invoice' : 'Quotation';

  // Client Info (defaults to clean state or URL params)
  const [client, setClient] = useState<ClientInfo>({
    name: searchParams.get('name') || searchParams.get('customerName') || '',
    phone: searchParams.get('phone') || '',
    email: searchParams.get('email') || '',
    address: searchParams.get('address') || '',
    gstin: searchParams.get('gstin') || searchParams.get('gstNumber') || '',
    reference: searchParams.get('reference') || '',
  });
  // Who prepared this document — a free-text field used to just default to
  // whoever's session created it, which is not always the actual BDM handling
  // the deal (e.g. an admin printing on someone's behalf). Offered as a pick
  // from the Sales team instead, same list the pipeline record's own
  // "Assigned Employee / BDM" dropdown uses, so the printed name is always a
  // real BDM rather than whoever happened to be logged in.
  const { assignees: bdmOptions } = useSalesAssignees();
  const [clientSearch, setClientSearch] = useState(searchParams.get('name') || searchParams.get('customerName') || '');
  const [clientSuggestions, setClientSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const clientDropdownRef = useRef<HTMLDivElement>(null);

  // Quotation auto-fill selection
  const [quotationsList, setQuotationsList] = useState<any[]>([]);
  const [quotationsLoading, setQuotationsLoading] = useState(true);
  const [quotationsError, setQuotationsError] = useState(false);
  const [quotationSearch, setQuotationSearch] = useState('');
  const [showQuotationDropdown, setShowQuotationDropdown] = useState(false);
  const quotationDropdownRef = useRef<HTMLDivElement>(null);

  // Billing Address
  const [billingAddress, setBillingAddress] = useState(searchParams.get('address') || '');

  // Shipping Address — was never captured anywhere on this page, so the
  // printed document always fell back to "same as billing" (the renderer's
  // own default when shipTo is blank). Defaults to following billing until
  // the rep explicitly says the delivery goes somewhere else.
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [shipCompanyName, setShipCompanyName] = useState('');
  const [shipStreet, setShipStreet] = useState('');
  const [shipCity, setShipCity] = useState('');
  const [shipState, setShipState] = useState('');
  const [shipZip, setShipZip] = useState('');
  const [shipPhone, setShipPhone] = useState('');
  const [shipEmail, setShipEmail] = useState('');

  // Meta
  // `number` starts blank rather than a random placeholder — the real
  // preview (matching what save will actually assign) loads a moment later
  // via the effect below. A random number here previously showed the user
  // a value with no relation to what got saved.
  const [meta, setMeta] = useState<DocMeta>({
    number: '',
    date: todayStr(),
    quotationNo: '',
    quotationDate: todayStr(),
    purchaseOrder: '',
    poDate: todayStr(),
    validUntil: '30 Days',
  });

  // Lines
  const [items, setItems] = useState<LineItem[]>([defaultLine(1)]);
  // Rate entry mode per row — same per-pack/per-piece/per-KG/per-LTR toggle
  // as the Sales Pipeline's product editor. Rate is always STORED as the
  // price of one whole pack; this only changes how it's typed in/displayed.
  const [rateModeByIndex, setRateModeByIndex] = useState<Record<number, 'pack' | 'piece' | 'base'>>({});

  // Tax & Dispatch
  const [cgstRate, setCgstRate] = useState(9.0);
  const [sgstRate, setSgstRate] = useState(9.0);
  const [dispatchDetails, setDispatchDetails] = useState('');
  const [dueDate, setDueDate] = useState('');

  // Terms — hardcoded fallback, replaced by the isDefault-flagged rows from
  // the Quotation Terms master once it loads (unless the user has already
  // edited/added/removed a term, tracked via termsTouchedRef).
  const [terms, setTerms] = useState<string[]>([
    '1. EX-FACTORY RATE',
  ]);
  const termsTouchedRef = useRef(false);

  // Words manual override
  const [wordsManual, setWordsManual] = useState('');

  // ── Tax Rates master (Master Data → Finance & Tax → Tax Rates) ─────────────
  const [taxRatePresets, setTaxRatePresets] = useState<{ _id: string; name: string; rate: number; type: string }[]>([]);
  const [selectedTaxPresetId, setSelectedTaxPresetId] = useState('');

  // ── Quotation Terms master (Master Data → Finance & Tax → Quotation Terms) ─
  const [termPresets, setTermPresets] = useState<{ _id: string; title: string; body: string; isDefault: boolean }[]>([]);
  const [termLibrarySelection, setTermLibrarySelection] = useState('');

  // ── Catalogue products ────────────────────────────────────────────────────
  const [catalogProducts, setCatalogProducts] = useState<CatalogProduct[]>([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [productSearch, setProductSearch] = useState<string[]>([]); // per-row search string
  const [productDropdown, setProductDropdown] = useState<number | null>(null); // which row is open
  const dropdownRef = useRef<HTMLDivElement>(null);

  // ── Auto-fill from Deal / Lead / Customer / URL query ──────────────────────
  useEffect(() => {
    const dealId = searchParams.get('dealId');
    const leadId = searchParams.get('leadId');
    const customerId = searchParams.get('customerId') || searchParams.get('clientId');

    if (dealId) {
      api.get(`/api/deals/${dealId}`)
        .then(({ data }) => {
          const deal = data?.deal || data;
          if (deal) {
            const dName = deal.contact_name || deal.business_name || deal.title || '';
            const dPhone = deal.phone || '';
            const dEmail = deal.email || '';
            // The record's own address field is the real source — the old
            // fallback here never actually read it, only business_name (already
            // used for name above) or service_required, so Address silently
            // stayed blank on every quotation launched from a pipeline record.
            const dAddr = deal.address || (deal.business_name && deal.business_name !== dName ? deal.business_name : (deal.service_required || ''));
            setClient(prev => ({
              ...prev,
              name: dName || prev.name,
              phone: dPhone || prev.phone,
              email: dEmail || prev.email,
              address: dAddr || prev.address,
              // The record owner's name — GET /api/deals/:id now returns
              // assigned_name (see server.js); previously it never did, so
              // this always fell through to the hardcoded 'BDM 6' placeholder.
              reference: deal.assigned_name || prev.reference,
            }));
            if (dAddr) setBillingAddress(dAddr);
            if (deal.shippingAddress) {
              const s = deal.shippingAddress;
              if (s.companyName || s.name || s.street || s.city || s.state || s.zipCode) {
                setSameAsBilling(false);
                setShipCompanyName(s.companyName || s.name || '');
                setShipStreet(s.street || '');
                setShipCity(s.city || '');
                setShipState(s.state || '');
                setShipZip(s.zipCode || '');
              }
            }
            if (dName) setClientSearch(dName);
            if (deal.service_required) {
              setMeta(prev => ({ ...prev, purchaseOrder: deal.service_required }));
            }
            // Carry the pipeline record's product lines onto the quotation
            // rather than starting from the placeholder default line — the
            // BDE already priced these while qualifying the enquiry.
            if (Array.isArray(deal.lines) && deal.lines.length > 0) {
              setItems(deal.lines.map((l: any, i: number) => ({
                sr: i + 1,
                hsn: l.hsnSac || '',
                product: l.description || l.productCode || '',
                productId: isRealObjectId(l.productId) ? l.productId : undefined,
                rate: Number(l.unitPrice) || 0,
                qty: Number(l.quantity) || 1,
                unit: l.unit || 'LTR',
                discount: Number(l.discount) || 0,
                discountType: l.discountType === 'fixed' ? 'fixed' : 'percent',
                packSize: l.packSize || '',
                qtyPerPack: Number(l.qtyPerPack) || undefined,
              })));
            }
          }
        })
        .catch(() => {});
    } else if (leadId) {
      api.get(`/api/leads/${leadId}`)
        .then(({ data }) => {
          const lead = data?.lead || data;
          if (lead) {
            const lName = lead.name || '';
            const lPhone = lead.phone || '';
            const lEmail = (lead.emails && lead.emails[0]) || '';
            const lAddr = lead.address || '';
            setClient(prev => ({
              ...prev,
              name: lName || prev.name,
              phone: lPhone || prev.phone,
              email: lEmail || prev.email,
              address: lAddr || prev.address,
            }));
            if (lAddr) setBillingAddress(lAddr);
            if (lName) setClientSearch(lName);
          }
        })
        .catch(() => {});
    } else if (customerId) {
      api.get(`/api/accounts/customers/${customerId}`)
        .then(({ data }) => {
          const cust = data?.customer || data;
          if (cust) {
            const addr = typeof cust.billingAddress === 'object'
              ? [cust.billingAddress?.street, cust.billingAddress?.city, cust.billingAddress?.state, cust.billingAddress?.pincode].filter(Boolean).join(', ')
              : cust.address || '';
            setClient({
              name: cust.name || '',
              phone: cust.phone || '',
              email: cust.email || '',
              address: addr,
              gstin: cust.gstNumber || cust.gstin || '',
              reference: cust.reference || '',
            });
            setBillingAddress(addr);
            setClientSearch(cust.name || '');
          }
        })
        .catch(() => {});
    }
  }, [searchParams]);

  // ── 1. Fetch Next Sequential Number Automatically on Mount ─────────────────
  useEffect(() => {
    api.get(`/api/documents/next-number?type=${docType}`)
      .then(({ data }) => {
        if (data?.nextNumber) {
          setMeta(prev => ({ ...prev, number: data.nextNumber }));
        }
      })
      .catch(() => {});
  }, [docType]);

  // ── 2. Load Quotations for Auto-fill ───────────────────────────────────────
  useEffect(() => {
    api.get('/api/documents/quotations-list')
      .then(({ data }) => {
        if (Array.isArray(data?.quotations)) {
          setQuotationsList(data.quotations);
        }
      })
      .catch(() => setQuotationsError(true))
      .finally(() => setQuotationsLoading(false));
  }, []);

  // ── 3. Load Catalogue Products on Mount ────────────────────────────────────
  useEffect(() => {
    api.get('/api/catalog/products?limit=500')
      .then(({ data }) => {
        const list: CatalogProduct[] = Array.isArray(data) ? data : (data.data || data.items || []);
        setCatalogProducts(list.length > 0 ? list : RBM_STATIC_PRODUCTS);
        setCatalogLoaded(true);
        setProductSearch(new Array(items.length).fill(''));
      })
      .catch(() => {
        setCatalogProducts(RBM_STATIC_PRODUCTS);
        setCatalogLoaded(true);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 3b. Load Tax Rate presets from Master Data ──────────────────────────────
  useEffect(() => {
    api.get('/api/masters/special/tax-rates?isActive=true')
      .then(({ data }) => {
        const list = Array.isArray(data?.items) ? data.items : [];
        setTaxRatePresets([...list].sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0)));
      })
      .catch(() => {});
  }, []);

  // ── 3c. Load Quotation Terms presets from Master Data ───────────────────────
  useEffect(() => {
    api.get('/api/masters/special/quotation-terms?isActive=true')
      .then(({ data }) => {
        const list: { _id: string; title: string; body: string; isDefault: boolean }[] = Array.isArray(data?.items) ? data.items : [];
        setTermPresets(list);
        const defaults = list.filter((t) => t.isDefault).map((t) => t.body).filter(Boolean);
        if (defaults.length > 0 && !termsTouchedRef.current) {
          setTerms(defaults.map((body, idx) => `${idx + 1}. ${body}`));
        }
      })
      .catch(() => {});
  }, []);

  // ── 4. Initial Parties Search on Mount ─────────────────────────────────────
  useEffect(() => {
    api.get('/api/documents/parties-search?limit=30')
      .then(({ data }) => {
        if (Array.isArray(data?.results)) {
          setClientSuggestions(data.results);
        }
      })
      .catch(() => {});
  }, []);

  // Expand productSearch array when rows change
  useEffect(() => {
    setProductSearch(prev => {
      const next = [...prev];
      while (next.length < items.length) next.push('');
      return next.slice(0, items.length);
    });
  }, [items.length]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setProductDropdown(null);
      }
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
      if (quotationDropdownRef.current && !quotationDropdownRef.current.contains(e.target as Node)) {
        setShowQuotationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectProduct = (rowIdx: number, product: CatalogProduct) => {
    // Packaging first — a product's real pack (Box/Drum/Barrel/Bucket/Can) is
    // what the catalogue actually stores per-product, while unitId is a
    // rarely-set generic UoM reference. Same rule as useQuotationForm.ts's
    // lineFromProduct.
    const unit = abbreviatePackaging(derivePackagingType(product)) || product.unitId?.shortName || product.unitId?.name || 'LTR';
    const rate = product.sellingPrice || product.mrp || 0;
    const hsn = product.hsnSac || product.hsnCode || '';
    setItems(prev => {
      const updated = [...prev];
      updated[rowIdx] = {
        ...updated[rowIdx],
        product: product.name,
        productId: isRealObjectId(product._id) ? product._id : undefined,
        hsn,
        rate,
        unit,
        packSize: product.packSize || '',
        qtyPerPack: product.qtyPerPack || undefined,
      };
      return updated;
    });
    setProductSearch(prev => {
      const updated = [...prev];
      updated[rowIdx] = product.name;
      return updated;
    });
    // Auto-fill tax rates from product profile
    if (product.taxProfileId?.cgstRate != null) setCgstRate(product.taxProfileId.cgstRate);
    if (product.taxProfileId?.sgstRate != null) setSgstRate(product.taxProfileId.sgstRate);
    setProductDropdown(null);
  };

  // UI state
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  // Search clients across Customers, Contacts, and Leads
  const searchClients = useCallback(async (query: string) => {
    try {
      const { data } = await api.get(`/api/documents/parties-search?q=${encodeURIComponent(query)}&limit=30`);
      setClientSuggestions(data.results || []);
      setShowSuggestions(true);
    } catch {
      setClientSuggestions([]);
    }
  }, []);

  const selectClient = (c: any) => {
    const addr = c.billingAddress || c.address || '';
    setClient({
      name: c.name || '',
      phone: c.phone || '',
      email: c.email || '',
      address: addr,
      gstin: c.gstin || c.gstNumber || '',
      reference: c.reference || '',
    });
    setBillingAddress(addr);
    const ship = c.shippingAddress;
    if (ship && (ship.companyName || ship.name || ship.street || ship.city || ship.state || ship.zipCode || ship.phone || ship.email)) {
      setSameAsBilling(false);
      setShipCompanyName(ship.companyName || ship.name || '');
      setShipStreet(ship.street || '');
      setShipCity(ship.city || '');
      setShipState(ship.state || '');
      setShipZip(ship.zipCode || '');
      setShipPhone(ship.phone || '');
      setShipEmail(ship.email || '');
    }
    setClientSearch(c.name || '');
    setShowSuggestions(false);
    setToast(`Auto-filled client: ${c.name}`);
    setTimeout(() => setToast(''), 3000);
  };

  // Copy a quotation into the current draft; retain its new document number/date.
  const selectQuotation = (q: any) => {
    const qDate = q.date ? new Date(q.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : todayStr();
    setMeta(prev => ({
      ...prev,
      quotationNo: q.number || prev.quotationNo,
      quotationDate: qDate,
      purchaseOrder: q.purchaseOrder || prev.purchaseOrder,
      poDate: q.poDate ? new Date(q.poDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : prev.poDate,
    }));

    if (q.client) {
      setClient({
        name: q.client.name || '',
        phone: q.client.phone || '',
        email: q.client.email || '',
        address: q.client.address || '',
        gstin: q.client.gstin || '',
        reference: q.client.reference || '',
      });
      setBillingAddress(q.client.address || '');
      setClientSearch(q.client.name || '');
    }

    if (Array.isArray(q.items) && q.items.length > 0) {
      setItems(q.items.map((it: any, idx: number) => ({
        sr: idx + 1,
        product: it.product || it.name || '',
        productId: isRealObjectId(it.productId) ? it.productId : undefined,
        hsn: it.hsn || '27101980',
        rate: Number(it.rate) || 0,
        qty: Number(it.qty) || 1,
        unit: it.unit || 'LTR',
        discount: Number(it.discount) || 0,
        discountType: it.discountType === 'fixed' ? 'fixed' : 'percent',
        packSize: it.packSize || '',
        qtyPerPack: Number(it.qtyPerPack) || undefined,
      })));
    }

    if (q.cgstRate != null) setCgstRate(q.cgstRate);
    if (q.sgstRate != null) setSgstRate(q.sgstRate);
    if (q.dispatchDetails) setDispatchDetails(q.dispatchDetails);

    setShowQuotationDropdown(false);
    setToast(`Auto-filled all details from Quotation ${q.number}!`);
    setTimeout(() => setToast(''), 3500);
  };

  // Line item manipulation
  const updateItem = (index: number, field: keyof LineItem, value: any) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addRow = () => {
    setItems((prev) => [...prev, { sr: prev.length + 1, hsn: '', product: '', rate: 0, qty: 1, unit: 'LTR', discount: 0, discountType: 'percent', packSize: '', qtyPerPack: undefined }]);
  };

  // The catalogue's own "[Category] Name — Size x count (Packing)" label,
  // same style as the reference template's product picker, built from
  // whatever taxonomy field this product actually has populated (division
  // is the newer, more specific one; category the older fallback).
  const catalogLabel = (p: CatalogProduct) => {
    const cat = p.divisionId?.name || p.categoryId?.name;
    const unit = abbreviatePackaging(derivePackagingType(p)) || p.unitId?.shortName || p.unitId?.name || 'LTR';
    const size = p.packSize ? `${p.packSize}${p.qtyPerPack && p.qtyPerPack > 1 ? ` x${p.qtyPerPack}` : ''}` : '';
    const bits = [size, `(${unit})`].filter(Boolean).join(' ');
    return `${cat ? `[${cat}] ` : ''}${p.name}${bits ? ` — ${bits}` : ''}`;
  };

  // Appends a new, fully-populated row from the top catalogue search bar —
  // the reference template's "+ Add from catalogue" flow — rather than
  // requiring an existing empty row to fill in first.
  const [catalogSearchValue, setCatalogSearchValue] = useState('');
  const addFromCatalog = () => {
    const label = catalogSearchValue.trim();
    const product = catalogProducts.find((p) => catalogLabel(p) === label);
    if (!product) {
      setToast('Pick a product from the catalogue list first');
      setTimeout(() => setToast(''), 2500);
      return;
    }
    const unit = abbreviatePackaging(derivePackagingType(product)) || product.unitId?.shortName || product.unitId?.name || 'LTR';
    setItems((prev) => [...prev, {
      sr: prev.length + 1,
      hsn: product.hsnSac || product.hsnCode || '',
      product: product.name,
      productId: isRealObjectId(product._id) ? product._id : undefined,
      rate: product.sellingPrice || product.mrp || 0,
      qty: 1,
      unit,
      discount: 0,
      discountType: 'percent',
      packSize: product.packSize || '',
      qtyPerPack: product.qtyPerPack || undefined,
    }]);
    if (product.taxProfileId?.cgstRate != null) setCgstRate(product.taxProfileId.cgstRate);
    if (product.taxProfileId?.sgstRate != null) setSgstRate(product.taxProfileId.sgstRate);
    setCatalogSearchValue('');
  };

  const removeRow = (index: number) => {
    setItems((prev) => {
      const filtered = prev.filter((_, i) => i !== index);
      return filtered.map((it, i) => ({ ...it, sr: i + 1 }));
    });
  };

  const addTerm = () => {
    termsTouchedRef.current = true;
    setTerms((prev) => [...prev, `${prev.length + 1}. `]);
  };

  const updateTerm = (index: number, val: string) => {
    termsTouchedRef.current = true;
    setTerms((prev) => {
      const updated = [...prev];
      updated[index] = val;
      return updated;
    });
  };

  const effectiveTerms = useMemo(() => {
    return (termPresets && termPresets.length > 0) ? termPresets : DEFAULT_FALLBACK_TERMS;
  }, [termPresets]);

  // Append a saved Quotation Terms library entry as a new numbered line.
  const addTermFromLibrary = (presetId: string) => {
    const preset = effectiveTerms.find((t) => t._id === presetId);
    if (!preset) return;
    termsTouchedRef.current = true;
    setTerms((prev) => [...prev, `${prev.length + 1}. ${preset.body}`]);
    setTermLibrarySelection('');
  };

  // Apply a Tax Rates master preset — cgst_sgst presets split their full
  // rate in half across CGST/SGST (this editor has no IGST field, so only
  // cgst_sgst presets are offered here; manual override remains available
  // via the CGST/SGST number inputs).
  const applyTaxPreset = (presetId: string) => {
    setSelectedTaxPresetId(presetId);
    const preset = taxRatePresets.find((t) => t._id === presetId);
    if (!preset) return;
    setCgstRate(preset.rate / 2);
    setSgstRate(preset.rate / 2);
  };

  // Calculations
  const calculations = useMemo(() => {
    let subtotal = 0;
    let discountTotal = 0;
    let totalQty = 0;
    const cgstPct = (cgstRate || 0) / 100;
    const sgstPct = (sgstRate || 0) / 100;
    const rowTotals = items.map((it) => {
      const gross = (Number(it.rate) || 0) * (Number(it.qty) || 0);
      const discountAmt = it.discountType === 'fixed'
        ? Math.min(gross, Number(it.discount) || 0)
        : gross * ((Number(it.discount) || 0) / 100);
      const total = gross - discountAmt;
      const gtotal = total * (1 + cgstPct + sgstPct);
      subtotal += total;
      discountTotal += discountAmt;
      totalQty += Number(it.qty) || 0;
      return { total, gtotal };
    });
    const cgst = subtotal * cgstPct;
    const sgst = subtotal * sgstPct;
    const tax = cgst + sgst;
    const grandTotal = subtotal + tax;
    const autoWords = numberToWordsIndian(grandTotal) + ' Only';
    return { subtotal, discountTotal, totalQty, cgst, sgst, tax, grandTotal, rowTotals, autoWords };
  }, [items, cgstRate, sgstRate]);

  // Save to database
  const handleSave = async () => {
    if (!client.name.trim()) {
      setToast('Please enter or select a client name');
      return;
    }
    if (items.length === 0 || items.every((l) => !l.product.trim())) {
      setToast('Please add at least one line item');
      return;
    }
    if (docType === 'invoice' && !meta.quotationNo && !searchParams.get('sourceId') && !searchParams.get('quotationId')) {
      setToast('Direct invoice creation is disabled. Please link an existing Quotation to generate this invoice.');
      return;
    }
    setSaving(true);
    try {
      // SalesOrder's documentType enum (src/db-sales.js) only knows
      // 'quotation' | 'proforma' | 'sales_order' | 'direct_sale' |
      // 'delivery_challan' — there is no 'invoice' member. A document
      // typed up directly here (not converted from an existing sales
      // order) is a direct sale, same mapping api-sales/orders.js already
      // uses for permissions (DOC_TYPE_TO_ENTITY.direct_sale = 'invoice').
      // Sending docType straight through as 'invoice' fails Mongoose
      // validation on every single save.
      const backendDocumentType = docType === 'invoice' ? 'direct_sale' : docType;
      const payload = {
        customerName: client.name,
        customerEmail: client.email,
        customerPhone: client.phone,
        documentType: backendDocumentType,
        date: meta.date,
        validUntil: meta.validUntil || undefined,
        notes: terms.join('\n'),
        crmDealId: searchParams.get('dealId') || undefined,
        crmLeadId: searchParams.get('leadId') || undefined,
        // Same {street,city,state,zipCode} shape as Bill To, so "Ship to"
        // prints as fully as "Bill to" does. Sent only when it actually
        // differs from billing; blank/omitted is exactly "same as billing"
        // to the renderer (its own default when shipTo is absent).
        shippingAddress: sameAsBilling || !(shipCompanyName.trim() || shipStreet.trim() || shipCity.trim() || shipState.trim() || shipZip.trim() || shipPhone.trim() || shipEmail.trim())
          ? undefined
          : {
              companyName: shipCompanyName.trim(),
              name: shipCompanyName.trim(),
              street: shipStreet.trim(),
              city: shipCity.trim(),
              state: shipState.trim(),
              zipCode: shipZip.trim(),
              phone: shipPhone.trim() || undefined,
              email: shipEmail.trim() || undefined,
            },
        lines: items
          .filter((l) => l.product.trim())
          .map((l) => {
            const gross = (Number(l.rate) || 0) * (Number(l.qty) || 0);
            const discountAmt = l.discountType === 'fixed'
              ? Math.min(gross, Number(l.discount) || 0)
              : gross * ((Number(l.discount) || 0) / 100);
            return {
              productId: l.productId || null,
              description: l.product,
              hsnSac: l.hsn,
              quantity: Number(l.qty) || 1,
              unitPrice: Number(l.rate) || 0,
              unit: l.unit || 'LTR',
              discount: Number(l.discount) || 0,
              discountType: l.discountType === 'fixed' ? 'fixed' : 'percent',
              lineTotal: gross - discountAmt,
              cgstRate,
              sgstRate,
              igstRate: 0,
              // Present on salesOrderLineSchema (src/db-sales.js) — omitting
              // these would silently drop a line's pack info the moment this
              // document is saved, the same class of bug already fixed once
              // this session in invoiceLineToSalesOrderLine.
              packSize: l.packSize || '',
              qtyPerPack: Number(l.qtyPerPack) || 0,
            };
          }),
        subTotal: calculations.subtotal,
        taxTotal: calculations.tax,
        grandTotal: calculations.grandTotal,
      };
      const { data: saved } = await api.post('/api/sales-ops/orders', payload);
      // The destination lists have a 30-second global stale window. Mark every
      // sales-document view stale before navigating so a newly saved document
      // cannot disappear behind a still-fresh pre-save cache.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sales-ops'] }),
        queryClient.invalidateQueries({ queryKey: ['quotations'] }),
        queryClient.invalidateQueries({ queryKey: ['proforma'] }),
        queryClient.invalidateQueries({ queryKey: ['invoice-requests'] }),
      ]);
      // The number shown while drafting (below) is only a rough look-ahead —
      // the real one is allocated atomically at save time and can differ if
      // another document was created in between. Swap in the real one so a
      // Print/Download right after Save never shows a number that was never
      // actually assigned to this document.
      if (saved?.orderNumber) {
        setMeta((prev) => ({ ...prev, number: saved.orderNumber }));
      }
      setToast(`${docTitle} saved successfully!`);
      setTimeout(() => {
        // '/accounting-v2/quote-to-cash' is a different system entirely
        // (SalesQuotationV2Model) — it would never show the direct_sale
        // document just saved here. Each doc type lands on its own list page.
        // `replace: true` — same reasoning as NewPipelineRecordPage's
        // post-create redirect: otherwise Back returns to this now-stale
        // Studio form with no trace the document was already saved.
        navigate(
          docType === 'invoice' ? '/invoices' : docType === 'proforma' ? '/proforma' : '/quotations',
          { replace: true },
        );
      }, 1500);
    } catch (err: any) {
      setToast(err.response?.data?.error || 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadHtml = () => {
    const sheet = document.getElementById('rbm-sheet');
    if (!sheet) return;
    const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${docTitle} ${meta.number} - RBM Oil Corporation</title>
<style>
  :root{
    --brand-accent:#34495E;
    --header-fill:#D2D6DE;
    --divider:#DDDDDD;
    --text:#222222;
    --muted:#5A6472;
  }
  *{box-sizing:border-box;}
  body{
    font-family:Arial,Helvetica,sans-serif;
    color:var(--text);
    background:#fff;
    margin:0;
    padding:20px;
  }
  .sheet{
    max-width:900px;
    margin:0 auto;
    background:#fff;
    padding:34px 38px 30px;
    font-size:12.5px;
    line-height:1.35;
  }
  .tagline{font-style:italic;color:var(--muted);font-size:11px;margin-bottom:10px;}
  .head{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;}
  .head img.logo{width:96px;height:auto;display:block;}
  .co-info{text-align:right;}
  .co-info .co-name{font-size:21px;font-weight:bold;letter-spacing:0.3px;margin-bottom:4px;}
  .co-info div{font-size:11.5px;color:#333;margin-top:1px;}
  .hr{border:none;border-top:2px solid #999;margin:14px 0 16px;}
  .info-grid{
    display:grid;
    grid-template-columns:1fr 1fr 1fr;
    gap:0;
    border:0;
    margin-bottom:16px;
  }
  .info-col{padding:2px 16px 2px 14px;}
  .info-col.customer{
    border-left:5px solid var(--brand-accent);
    padding-left:12px;
  }
  .info-col.billing{border-left:1px solid var(--divider);}
  .info-col.meta{border-left:1px solid var(--divider);text-align:right;}
  .row-line{display:flex;gap:6px;margin-bottom:6px;}
  .row-line .lab{font-weight:bold;white-space:nowrap;min-width:58px;}
  .row-line.meta-line{justify-content:flex-end;}
  .row-line.meta-line .lab{min-width:auto;}
  .cust-name{font-weight:bold;font-size:13.5px;}
  .doc-title{font-size:24px;font-weight:bold;margin-bottom:2px;}
  .doc-number{font-size:15px;font-weight:bold;color:var(--brand-accent);margin-bottom:10px;}
  table.items{width:100%;border-collapse:collapse;margin-bottom:0;}
  table.items thead th{
    background:var(--brand-accent);color:#fff;font-size:11.5px;
    padding:7px 6px;text-align:center;border:1px solid var(--brand-accent);
  }
  table.items thead th.left{text-align:left;}
  table.items tbody td{
    border-bottom:1px solid var(--divider);
    padding:6px 6px;
    font-size:12px;
    vertical-align:top;
  }
  table.items tbody td.num{text-align:right;}
  table.items tbody td.center{text-align:center;}
  .summary{margin-top:6px;}
  .summary-row{
    display:flex;justify-content:flex-end;align-items:center;
    padding:5px 0;border-bottom:1px solid var(--divider);
    gap:14px;
  }
  .summary-row .s-label{font-weight:bold;min-width:110px;text-align:right;}
  .summary-row .s-value{min-width:120px;text-align:right;}
  .summary-row.rate-row .s-label{font-weight:normal;color:#444;}
  .summary-row.dispatch-row{justify-content:space-between;}
  .summary-row.grand{
    background:var(--header-fill);
    font-size:14px;font-weight:bold;
    border-bottom:none;
    padding:8px 10px;
    margin-top:2px;
  }
  .due-date-row{
    display:flex;justify-content:space-between;align-items:center;
    padding:5px 10px 5px 0;
  }
  .words-row{
    display:flex;gap:8px;margin-top:6px;font-size:12px;
  }
  .words-row .lab{font-weight:bold;white-space:nowrap;}
  .tc-title{
    background:var(--header-fill);text-align:center;font-weight:bold;
    padding:6px 0;margin-top:22px;font-size:13px;
  }
  .tc-list{margin:0;padding:0;list-style:none;}
  .tc-list li{padding:5px 4px;border-bottom:1px solid var(--divider);font-size:12px;}
  .pay-title{font-weight:bold;margin-top:16px;margin-bottom:6px;font-size:13px;}
  .pay-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:6px 10px;font-size:12px;margin-bottom:6px;}
  .pay-grid .lab{font-weight:bold;margin-right:4px;}
  .swift-row{font-size:12px;margin-top:4px;}
  .swift-row .lab{font-weight:bold;margin-right:4px;}
  .footer{
    display:flex;justify-content:space-between;
    border-top:2px solid #ccc;margin-top:20px;padding-top:10px;
    font-size:11px;color:#333;
  }
  .footer .website{color:var(--brand-accent);}
  @page{size:A4;margin:10mm;}
</style>
</head>
<body>
<div class="sheet">
${sheet.innerHTML}
</div>
</body>
</html>`;
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${docTitle}_${meta.number.replace(/[^a-zA-Z0-9]/g, '_')}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="min-h-screen bg-[#e9edf1] print:bg-white text-[#222]"
      style={{
        fontFamily: 'Arial, Helvetica, sans-serif',
      }}
    >
      {/* ── TOP TOOLBAR ────────────────────────────────────────────────────── */}
      <div className="print:hidden sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm px-6 py-3 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>

          {/* Document Type Switcher */}
          <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl border border-slate-200">
            <button
              onClick={() => setSearchParams({ type: 'quotation' })}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                docType === 'quotation'
                  ? 'bg-[#34495E] text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'
              }`}
            >
              Quotation
            </button>
            <button
              onClick={() => setSearchParams({ type: 'proforma' })}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                docType === 'proforma'
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'
              }`}
            >
              Proforma Invoice
            </button>
            {docType === 'invoice' && (
              <span
                className="px-3 py-1.5 text-xs font-bold rounded-lg bg-success-text text-on-primary shadow-sm"
              >
                Tax Invoice (from Quote/Proforma)
              </span>
            )}
          </div>
          <span className="text-sm font-bold text-slate-800">RBM {docTitle} Studio</span>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()} className="gap-1.5 font-medium">
            <Printer className="w-4 h-4" /> Print / Save as PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadHtml} className="gap-1.5 font-medium">
            <Download className="w-4 h-4" /> Download .html
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="gap-1.5 bg-[#34495E] hover:bg-[#2c3e50] text-white font-bold shadow-sm"
          >
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : `Save & Issue ${docTitle}`}
          </Button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="print:hidden fixed top-16 right-6 z-50 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm shadow-lg animate-in fade-in">
          {toast}
          <button onClick={() => setToast('')} className="ml-3 opacity-60 hover:opacity-100">
            &times;
          </button>
        </div>
      )}

      {/* Auto-fill is available for quotations as well as invoices/proformas. */}
      {docType === 'invoice' && !meta.quotationNo && (
        <div className="max-w-[900px] mx-auto mb-3 print:hidden bg-warning/10 border border-warning/25 rounded-xl p-3 px-4 text-xs text-warning-text shadow-xs flex items-center gap-2">
          <span className="font-bold text-warning-text text-sm">⚠️</span>
          <div>
            <strong>Direct Invoice creation is disabled:</strong> Tax Invoices must originate from an existing Quotation or Proforma. Please pick a Quotation using the auto-fill bar below to populate details.
          </div>
        </div>
      )}
      {(
        <div className="max-w-[900px] mx-auto mb-3 print:hidden bg-info/10 border border-info/25 rounded-xl p-2.5 px-4 flex flex-wrap items-center justify-between gap-3 text-xs shadow-xs">
          <div className="flex items-center gap-2 text-info-text font-medium">
            <span className="bg-[#34495E] text-white rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">⚡ Auto-Fill</span>
            <span>Select an existing Quotation to auto-fill Customer, Products, Rates & Taxes:</span>
          </div>
          <input
            type="search"
            value={quotationSearch}
            onChange={(event) => setQuotationSearch(event.target.value)}
            placeholder="Search quotation number or client…"
            aria-label="Search quotations for auto-fill"
            className="min-w-[230px] rounded-lg border border-info/25 bg-white px-3 py-1.5 text-xs text-gray-800"
          />
          <select
            aria-label="Choose quotation to auto-fill"
            disabled={quotationsLoading || quotationsError || quotationsList.length === 0}
            className="bg-white border border-info/25 rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-info/40 cursor-pointer shadow-xs"
            onChange={(e) => {
              const selected = quotationsList.find(q => String(q.id || q.number) === e.target.value);
              if (selected) selectQuotation(selected);
            }}
            value=""
          >
            <option value="" disabled>{quotationsLoading ? 'Loading quotations…' : quotationsError ? 'Could not load quotations — refresh to retry' : quotationsList.length === 0 ? 'No saved quotations yet' : '-- Choose Quotation --'}</option>
            {quotationsList.filter(q => `${q.number || ''} ${q.client?.name || ''}`.toLowerCase().includes(quotationSearch.trim().toLowerCase())).map((q, idx) => (
              <option key={q.id || idx} value={String(q.id || q.number)}>
                {q.number} — {q.client?.name || 'Client'} ({q.items?.length || 0} items)
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ── INVOICE SHEET (Matches exact provided template) ───────────────── */}
      <div
        id="rbm-sheet"
        className="max-w-[900px] mx-auto my-6 print:my-0 bg-white shadow-md print:shadow-none p-[34px_38px_30px] print:p-[14px_22px] text-[12.5px] leading-[1.35]"
      >
        {/* Tagline */}
        <div className="italic text-[#5A6472] text-[11px] mb-[10px]">
          Energy Saved is Energy Produced!
        </div>

        {/* Head */}
        <div className="flex justify-between items-start gap-5">
          <img
            className="w-[96px] h-auto block"
            src={RBM_LOGO_BASE64}
            alt="RBM Lubrifiants logo"
          />
          <div className="text-right">
            <div className="text-[21px] font-bold tracking-[0.3px] mb-1">
              RBM OIL CORPORATION
            </div>
            <div className="text-[11.5px] text-[#333] mt-[1px]">Sr. No. 42 Rasikwadi, Jambe Road, Pune-411033</div>
            <div className="text-[11.5px] text-[#333] mt-[1px]">sales@rbmoilcorporation.in</div>
            <div className="text-[11.5px] text-[#333] mt-[1px]">9146588595</div>
            <div className="text-[11.5px] text-[#333] mt-[1px]">GSTIN: 27AYJPD3083D1ZN</div>
            <div className="text-[11.5px] text-[#333] mt-[1px]">PAN No: AYJPD3083D</div>
          </div>
        </div>

        <hr className="border-none border-t-2 border-[#999] my-[14px] mb-[16px]" />

        {/* Info Grid */}
        <div className="grid grid-cols-3 gap-0 mb-4">
          {/* Col 1: Customer */}
          <div className="px-4 py-[2px] border-l-[5px] border-[#34495E] pl-3">
            <div className="flex gap-1.5 mb-1.5 relative" ref={clientDropdownRef}>
              <span className="font-bold whitespace-nowrap min-w-[58px]">Name :</span>
              <div className="flex-1 relative">
                <div className="flex items-center">
                  <input
                    value={clientSearch || client.name}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setClient((p) => ({ ...p, name: e.target.value }));
                      searchClients(e.target.value);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    placeholder="Search or enter customer..."
                    className="font-bold text-[13.5px] w-full bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
                  />
                  <ChevronDown
                    className="w-3.5 h-3.5 text-gray-400 cursor-pointer hover:text-gray-700 print:hidden shrink-0"
                    onClick={() => {
                      setShowSuggestions(!showSuggestions);
                      if (!showSuggestions && clientSuggestions.length === 0) {
                        searchClients('');
                      }
                    }}
                  />
                </div>
                {showSuggestions && (
                  <div className="print:hidden absolute top-full left-0 z-50 w-72 bg-white border border-gray-300 rounded-lg shadow-xl max-h-56 overflow-auto">
                    <div className="p-2 border-b border-gray-100 bg-gray-50 text-[10px] font-bold text-gray-500 flex justify-between items-center">
                      <span>CHOOSE CUSTOMER / LEAD</span>
                      <span className="text-[10px] text-gray-400">{clientSuggestions.length} found</span>
                    </div>
                    {clientSuggestions.map((c, idx) => (
                      <button
                        key={c.id || idx}
                        onClick={() => selectClient(c)}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-info/10 border-b border-gray-50 flex flex-col transition"
                      >
                        <div className="font-semibold text-gray-900">{c.name}</div>
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          {c.phone && <span>📞 {c.phone} </span>}
                          {c.gstin && <span className="ml-1 text-info-text font-mono">GST: {c.gstin}</span>}
                        </div>
                        {c.address && (
                          <div className="text-[10px] text-gray-400 truncate max-w-[260px]">{c.address}</div>
                        )}
                      </button>
                    ))}
                    {clientSuggestions.length === 0 && (
                      <div className="px-3 py-4 text-center text-xs text-gray-400">No matching customer. Type to enter.</div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-1.5 mb-1.5">
              <span className="font-bold whitespace-nowrap min-w-[58px]">Phone :</span>
              <input
                value={client.phone}
                onChange={(e) => setClient((p) => ({ ...p, phone: e.target.value }))}
                className="w-full bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
              />
            </div>
            <div className="flex gap-1.5 mb-1.5">
              <span className="font-bold whitespace-nowrap min-w-[58px]">Email :</span>
              <input
                value={client.email}
                onChange={(e) => setClient((p) => ({ ...p, email: e.target.value }))}
                className="w-full bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
              />
            </div>
            <div className="flex gap-1.5 mb-1.5">
              <span className="font-bold whitespace-nowrap min-w-[58px]">Address :</span>
              <textarea
                value={client.address}
                rows={2}
                onChange={(e) => setClient((p) => ({ ...p, address: e.target.value }))}
                className="w-full bg-transparent resize-none rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
              />
            </div>
            <div className="flex gap-1.5 mb-1.5">
              <span className="font-bold whitespace-nowrap min-w-[58px]">GSTIN :</span>
              <input
                value={client.gstin}
                onChange={(e) => setClient((p) => ({ ...p, gstin: e.target.value }))}
                className="w-full bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
              />
            </div>
            <div className="flex gap-1.5 mb-1.5">
              <span className="font-bold whitespace-nowrap min-w-[58px]">Prepared by :</span>
              <select
                value={client.reference}
                onChange={(e) => setClient((p) => ({ ...p, reference: e.target.value }))}
                className="w-full bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
              >
                <option value="">— Select —</option>
                {/* A value already on the record that isn't in the current
                    Sales list (a deactivated user, or a name typed before
                    this became a dropdown) stays selectable instead of
                    silently disappearing. Shows the login USERNAME
                    (bdm1/bdm2/bdm3...), not the display name — this is a BDM
                    id tag on the document, not a personal name. */}
                {client.reference && !bdmOptions.some((u) => u.username === client.reference) && (
                  <option value={client.reference}>{client.reference}</option>
                )}
                {bdmOptions.map((u) => (
                  <option key={u.id} value={u.username}>{u.username}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Col 2: Billing Address */}
          <div className="px-4 py-[2px] border-l border-[#DDDDDD]">
            <div className="font-bold mb-1.5">Billing Address:</div>
            <textarea
              value={billingAddress}
              rows={4}
              onChange={(e) => setBillingAddress(e.target.value)}
              className="w-full bg-transparent resize-none rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
            />
            <label className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold text-gray-600">
              <input
                type="checkbox"
                checked={sameAsBilling}
                onChange={(e) => {
                  const next = e.target.checked;
                  setSameAsBilling(next);
                  if (!next && !shipCompanyName) {
                    setShipCompanyName(client.name || '');
                  }
                }}
              />
              Shipping address same as billing
            </label>
            {sameAsBilling ? (
              <div className="mt-1.5 text-[11px] text-gray-500">
                {billingAddress || 'Same address as billing.'}
              </div>
            ) : (
              <>
                <div className="font-bold mb-1.5 mt-1.5">Shipping Address:</div>
                <input
                  value={shipCompanyName}
                  placeholder="Company / Consignee Name (Ship To)"
                  onChange={(e) => setShipCompanyName(e.target.value)}
                  className="mb-1 w-full bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 text-xs font-semibold print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
                />
                <textarea
                  value={shipStreet}
                  rows={2}
                  placeholder="Delivery address / street"
                  onChange={(e) => setShipStreet(e.target.value)}
                  className="w-full bg-transparent resize-none rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
                />
                <div className="mt-1 grid grid-cols-3 gap-1.5">
                  <input
                    value={shipCity}
                    placeholder="City"
                    onChange={(e) => setShipCity(e.target.value)}
                    className="w-full bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
                  />
                  <input
                    value={shipState}
                    placeholder="State"
                    onChange={(e) => setShipState(e.target.value)}
                    className="w-full bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
                  />
                  <input
                    value={shipZip}
                    placeholder="Pincode"
                    onChange={(e) => setShipZip(e.target.value)}
                    className="w-full bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
                  />
                </div>
                <div className="mt-1 grid grid-cols-2 gap-1.5">
                  <input
                    value={shipPhone}
                    placeholder="Mobile / Phone (Ship To) - optional"
                    onChange={(e) => setShipPhone(e.target.value)}
                    className="w-full bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
                  />
                  <input
                    value={shipEmail}
                    placeholder="Email (Ship To) - optional"
                    onChange={(e) => setShipEmail(e.target.value)}
                    className="w-full bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
                  />
                </div>
              </>
            )}
          </div>

          {/* Col 3: Meta */}
          <div className="px-4 py-[2px] border-l border-[#DDDDDD] text-right">
            <div className="text-[24px] font-bold mb-0.5">{docTitle}</div>
            <div className="text-[15px] font-bold text-[#34495E] mb-2.5">
              <input
                value={meta.number}
                onChange={(e) => setMeta((p) => ({ ...p, number: e.target.value }))}
                placeholder="Generating…"
                className="text-right font-bold text-[#34495E] bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
              />
            </div>
            <div className="flex justify-end gap-1.5 mb-1.5">
              <span className="font-bold">Date :</span>
              <input
                value={meta.date}
                onChange={(e) => setMeta((p) => ({ ...p, date: e.target.value }))}
                className="text-right w-28 bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
              />
            </div>
            <div className="flex justify-end gap-1.5 mb-1.5 relative" ref={quotationDropdownRef}>
              <span className="font-bold">Quotation No :</span>
              <div className="relative">
                <div className="flex items-center justify-end gap-1">
                  <input
                    value={meta.quotationNo}
                    onChange={(e) => setMeta((p) => ({ ...p, quotationNo: e.target.value }))}
                    onFocus={() => quotationsList.length > 0 && setShowQuotationDropdown(true)}
                    className="text-right w-28 bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
                  />
                  {quotationsList.length > 0 && (
                    <span title="Select quotation to auto-fill" className="print:hidden shrink-0">
                      <ChevronDown
                        className="w-3.5 h-3.5 text-gray-400 cursor-pointer hover:text-gray-700"
                        onClick={() => setShowQuotationDropdown(!showQuotationDropdown)}
                      />
                    </span>
                  )}
                </div>
                {showQuotationDropdown && (
                  <div className="print:hidden absolute top-full right-0 z-50 w-72 bg-white border border-gray-300 rounded-lg shadow-xl max-h-56 overflow-auto text-left">
                    <div className="p-2 border-b border-gray-100 bg-gray-50 text-[10px] font-bold text-gray-500 flex justify-between items-center">
                      <span>SELECT QUOTATION TO AUTO-FILL</span>
                    </div>
                    {quotationsList.map((q, idx) => (
                      <button
                        key={q.id || idx}
                        onClick={() => selectQuotation(q)}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-info/10 border-b border-gray-50 flex flex-col transition"
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-[#34495E]">{q.number}</span>
                          <span className="text-[10px] text-gray-400">{q.date ? new Date(q.date).toLocaleDateString('en-GB') : ''}</span>
                        </div>
                        <div className="text-[11px] font-medium text-gray-700 truncate mt-0.5">{q.client?.name || 'Customer'}</div>
                        <div className="text-[10px] text-gray-500">
                          {q.items?.length || 0} item(s) · Auto-fills lines & client
                        </div>
                      </button>
                    ))}
                    {quotationsList.length === 0 && (
                      <div className="px-3 py-4 text-center text-xs text-gray-400">No saved quotations available</div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-1.5 mb-1.5">
              <span className="font-bold">Quotation Date :</span>
              <input
                value={meta.quotationDate}
                onChange={(e) => setMeta((p) => ({ ...p, quotationDate: e.target.value }))}
                className="text-right w-28 bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
              />
            </div>
            <div className="flex justify-end gap-1.5 mb-1.5">
              <span className="font-bold">Purchase Order :</span>
              <input
                value={meta.purchaseOrder}
                onChange={(e) => setMeta((p) => ({ ...p, purchaseOrder: e.target.value }))}
                className="text-right w-28 bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
              />
            </div>
            <div className="flex justify-end gap-1.5 mb-1.5">
              <span className="font-bold">P.O Date :</span>
              <input
                value={meta.poDate}
                onChange={(e) => setMeta((p) => ({ ...p, poDate: e.target.value }))}
                className="text-right w-28 bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
              />
            </div>
          </div>
        </div>

        {/* ── CATALOGUE QUICK-ADD ──────────────────────────────────────────────
            Appends a new, fully-populated row straight from the real catalogue
            (name, HSN, rate, unit, pack size, pack count) — the per-row dropdown
            further below still exists to *replace* an already-added row's
            product. */}
        <div className="flex gap-1.5 mb-1.5 print:hidden">
          <input
            list="rbm-catalog-datalist"
            value={catalogSearchValue}
            onChange={(e) => setCatalogSearchValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFromCatalog(); } }}
            placeholder="🔍 Search RBM product to add (e.g. ALFA 10W30, or 900 ML box)…"
            className="flex-1 text-[11.5px] px-2.5 py-1.5 border border-gray-300 rounded focus:border-[#34495E] focus:outline-none"
          />
          <datalist id="rbm-catalog-datalist">
            {catalogProducts.map((p) => (
              <option key={p._id} value={catalogLabel(p)} />
            ))}
          </datalist>
          <Button
            type="button"
            onClick={addFromCatalog}
            className="gap-1.5 bg-[#34495E] hover:bg-[#2c3e50] text-white text-[11.5px] font-bold shadow-sm"
          >
            + Add from catalogue
          </Button>
        </div>

        {/* ── ITEMS TABLE ──────────────────────────────────────────────────── */}
        <table className="w-full border-collapse mb-0">
          <thead>
            <tr>
              <th className="bg-[#34495E] text-white text-[11.5px] p-[7px_6px] text-center border border-[#34495E] w-[4%]">
                Sr. No.
              </th>
              <th className="bg-[#34495E] text-white text-[11.5px] p-[7px_6px] text-center border border-[#34495E] w-[6%]">
                Marks
              </th>
              <th className="bg-[#34495E] text-white text-[11.5px] p-[7px_6px] text-center border border-[#34495E] w-[9%]">
                HSN
              </th>
              <th className="bg-[#34495E] text-white text-[11.5px] p-[7px_6px] text-left border border-[#34495E] w-[19%]">
                Product / Service
              </th>
              <th className="bg-[#34495E] text-white text-[11.5px] p-[7px_6px] text-center border border-[#34495E] w-[7%]">
                Qty
              </th>
              <th className="bg-[#34495E] text-white text-[11.5px] p-[7px_6px] text-center border border-[#34495E] w-[8%]">
                Packing
              </th>
              <th className="bg-[#34495E] text-white text-[11.5px] p-[7px_6px] text-center border border-[#34495E] w-[8%]">
                Size
              </th>
              <th className="bg-[#34495E] text-white text-[11.5px] p-[7px_6px] text-center border border-[#34495E] w-[6%]">
                Pack
              </th>
              <th className="bg-[#34495E] text-white text-[11.5px] p-[7px_6px] text-center border border-[#34495E] w-[9%]">
                Rate
              </th>
              <th className="bg-[#34495E] text-white text-[11.5px] p-[7px_6px] text-center border border-[#34495E] w-[6%]">
                Disc
              </th>
              <th className="bg-[#34495E] text-white text-[11.5px] p-[7px_6px] text-right border border-[#34495E] w-[10%]">
                Total
              </th>
              <th className="bg-[#34495E] text-white text-[11.5px] p-[7px_6px] text-right border border-[#34495E] w-[10%]">
                Grand Total
              </th>
              <th className="bg-[#34495E] text-white text-[11.5px] p-[7px_6px] text-center border border-[#34495E] w-[4%] print:hidden"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => {
              const rowTotal = calculations.rowTotals[idx] || { total: 0, gtotal: 0 };
              return (
                <tr key={idx} className="border-b border-[#DDDDDD]">
                  <td className="p-[6px] text-[12px] text-center">
                    <input
                      type="number"
                      value={it.sr}
                      onChange={(e) => updateItem(idx, 'sr', parseInt(e.target.value) || idx + 1)}
                      className="w-8 text-center bg-transparent focus:outline-none"
                    />
                  </td>
                  <td className="p-[6px] text-[12px] text-center text-gray-500">RBM</td>
                  <td className="p-[6px] text-[12px] text-center">
                    <input
                      value={it.hsn}
                      onChange={(e) => updateItem(idx, 'hsn', e.target.value)}
                      className="w-full text-center bg-transparent focus:outline-none"
                    />
                  </td>
                  <td className="p-[6px] text-[12px] text-left relative">
                    <div ref={productDropdown === idx ? dropdownRef : undefined} className="relative">
                      <div
                        className="flex items-center gap-1 cursor-pointer group"
                        onClick={() => setProductDropdown(productDropdown === idx ? null : idx)}
                      >
                        <span className="flex-1 font-medium truncate min-w-0">
                          {it.product || <span className="text-gray-400 italic">-- Choose Catalogue Lubricant --</span>}
                        </span>
                        <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      </div>
                      {productDropdown === idx && (
                        <div className="print:hidden absolute top-full left-0 z-50 w-80 bg-white border border-gray-300 rounded-lg shadow-xl">
                          <div className="p-2 border-b border-gray-100">
                            <div className="flex items-center gap-1.5 bg-gray-50 rounded px-2 py-1.5">
                              <Search className="w-3.5 h-3.5 text-gray-400" />
                              <input
                                autoFocus
                                value={productSearch[idx] || ''}
                                onChange={(e) => {
                                  const updated = [...productSearch];
                                  updated[idx] = e.target.value;
                                  setProductSearch(updated);
                                }}
                                placeholder="Search lubricant..."
                                className="w-full bg-transparent text-xs focus:outline-none"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          <div className="max-h-80 overflow-auto">
                            {catalogProducts
                              .filter(p => {
                                const q = (productSearch[idx] || '').toLowerCase();
                                return !q || p.name.toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q);
                              })
                              .map((p) => (
                                <button
                                  key={p._id}
                                  onClick={(e) => { e.stopPropagation(); selectProduct(idx, p); }}
                                  className="w-full text-left px-3 py-2 text-[11.5px] hover:bg-info/10 border-b border-gray-50 flex flex-col"
                                >
                                  <span className="font-semibold text-gray-800">{p.name}</span>
                                  <span className="text-gray-400 text-[10px]">
                                    SKU: {p.sku || '—'} · HSN: {p.hsnSac || p.hsnCode || '—'} · ₹{p.sellingPrice || p.mrp || 0} / {abbreviatePackaging(derivePackagingType(p)) || p.unitId?.shortName || p.unitId?.name || 'LTR'}
                                  </span>
                                </button>
                              ))
                            }
                            {catalogLoaded && catalogProducts.filter(p => {
                              const q = (productSearch[idx] || '').toLowerCase();
                              return !q || p.name.toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q);
                            }).length === 0 && (
                              <div className="px-3 py-4 text-center text-xs text-gray-400">No products found</div>
                            )}
                            {!catalogLoaded && (
                              <div className="px-3 py-4 text-center text-xs text-gray-400">Loading…</div>
                            )}
                          </div>
                          <div className="p-2 border-t border-gray-100">
                            <input
                              value={it.product}
                              onChange={(e) => {
                                updateItem(idx, 'product', e.target.value);
                                const updated = [...productSearch];
                                updated[idx] = e.target.value;
                                setProductSearch(updated);
                              }}
                              placeholder="Or type custom product name..."
                              className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded focus:border-[#34495E] focus:outline-none"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-[6px] text-[12px] text-right">
                    <input
                      type="number"
                      step="any"
                      value={it.qty}
                      onChange={(e) => updateItem(idx, 'qty', parseFloat(e.target.value) || 0)}
                      className="w-full text-right bg-transparent focus:outline-none"
                    />
                  </td>
                  <td className="p-[6px] text-[12px] text-center">
                    <UnitPackCell
                      unit={it.unit}
                      unitPrice={it.rate}
                      packSize={it.packSize}
                      onChange={({ unit, unitPrice }) => {
                        setItems((prev) => {
                          const updated = [...prev];
                          updated[idx] = { ...updated[idx], unit, rate: unitPrice };
                          return updated;
                        });
                      }}
                    />
                  </td>
                  <td className="p-[6px] text-[12px] text-center">
                    <input
                      value={it.packSize || ''}
                      onChange={(e) => updateItem(idx, 'packSize', e.target.value)}
                      placeholder="e.g. 900 ML"
                      className="w-full text-center bg-transparent focus:outline-none placeholder:text-gray-300"
                    />
                  </td>
                  <td className="p-[6px] text-[12px] text-center">
                    <input
                      type="number"
                      step="1"
                      value={it.qtyPerPack ?? ''}
                      onChange={(e) => updateItem(idx, 'qtyPerPack', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                      placeholder="1"
                      className="w-full text-center bg-transparent focus:outline-none placeholder:text-gray-300"
                    />
                  </td>
                  <td className="p-[6px] text-[12px] text-right">
                    {(() => {
                      const perPack = piecesPerPack(it);
                      const hasMultiPack = perPack > 1;
                      const unitDiv = baseUnitDivisor(it);
                      const hasBaseUnit = !!unitDiv;
                      const mode = (hasMultiPack || hasBaseUnit) ? (rateModeByIndex[idx] || 'pack') : 'pack';
                      const divisor = mode === 'piece' ? perPack : mode === 'base' ? (unitDiv?.qty || 1) : 1;
                      const stored = Number(it.rate) || 0;
                      const displayed = mode !== 'pack' ? Math.round((stored / divisor) * 100) / 100 : stored;
                      return (
                        <>
                          <input
                            type="number"
                            step="any"
                            value={displayed}
                            onChange={(e) => {
                              const typed = parseFloat(e.target.value) || 0;
                              updateItem(idx, 'rate', mode !== 'pack' ? typed * divisor : typed);
                            }}
                            className="w-full text-right bg-transparent focus:outline-none"
                          />
                          {(hasMultiPack || hasBaseUnit) && (
                            <select
                              value={mode}
                              onChange={(e) => setRateModeByIndex((prev) => ({ ...prev, [idx]: e.target.value as 'pack' | 'piece' | 'base' }))}
                              className="mt-0.5 w-full rounded border-0 bg-transparent text-[9px] font-medium text-gray-500 focus:outline-none cursor-pointer"
                            >
                              <option value="pack">per pack{hasMultiPack ? ` (×${perPack})` : ''}</option>
                              {hasMultiPack && (
                                <option value="piece">per piece{it.packSize ? ` (${it.packSize})` : ''}</option>
                              )}
                              {hasBaseUnit && (
                                <option value="base">per {unitDiv.unit} (×{unitDiv.qty})</option>
                              )}
                            </select>
                          )}
                        </>
                      );
                    })()}
                  </td>
                  <td className="p-[6px] text-[12px] text-right">
                    <div className="flex items-center gap-1">
                      <select
                        value={it.discountType || 'percent'}
                        onChange={(e) => updateItem(idx, 'discountType', e.target.value as 'percent' | 'fixed')}
                        className="w-8 bg-transparent text-[11px] font-semibold focus:outline-none"
                        aria-label="Discount type"
                      >
                        <option value="percent">%</option>
                        <option value="fixed">₹</option>
                      </select>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        max={it.discountType === 'fixed' ? undefined : 100}
                        value={it.discount || 0}
                        onChange={(e) => updateItem(idx, 'discount', parseFloat(e.target.value) || 0)}
                        className="min-w-0 flex-1 text-right bg-transparent focus:outline-none"
                      />
                    </div>
                  </td>
                  <td className="p-[6px] text-[12px] text-right font-medium">
                    {fmtWestern(rowTotal.total)}
                  </td>
                  <td className="p-[6px] text-[12px] text-right font-semibold text-[#34495E]">
                    {fmtWestern(rowTotal.gtotal)}
                  </td>
                  <td className="p-[6px] text-center print:hidden">
                    <button
                      onClick={() => removeRow(idx)}
                      className="bg-[#fff0f0] border border-[#e3b8b8] text-[#a33] text-[10px] rounded px-1.5 py-0.5 hover:bg-danger/10"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="print:hidden">
              <td colSpan={13} className="p-[8px_2px]">
                <button
                  onClick={addRow}
                  className="bg-[#f2f8fd] border border-dashed border-[#9cc4e0] text-[#0069a3] text-[11.5px] rounded px-2.5 py-1 hover:bg-info/10"
                >
                  + Add line item
                </button>
              </td>
            </tr>
          </tfoot>
        </table>

        {/* ── SUMMARY SECTION ──────────────────────────────────────────────── */}
        <div className="mt-1.5">
          <div className="flex justify-between items-center py-1 border-b border-[#DDDDDD] gap-3.5">
            <span>
              <b>Dispatch Details:</b>{' '}
              <input
                value={dispatchDetails}
                onChange={(e) => setDispatchDetails(e.target.value)}
                className="bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0 w-64 ml-1"
              />
            </span>
            <div className="flex items-center gap-3.5">
              <span className="font-bold min-w-[110px] text-right">Total:</span>
              <span className="min-w-[120px] text-right font-medium">{fmtWestern(calculations.subtotal)}</span>
            </div>
          </div>

          <div className="flex justify-end items-center py-1 border-b border-[#DDDDDD] gap-3.5">
            <span className="font-bold min-w-[110px] text-right">Total Quantity:</span>
            <span className="min-w-[120px] text-right font-medium">{calculations.totalQty}</span>
          </div>

          {calculations.discountTotal > 0 && (
            <div className="flex justify-end items-center py-1 border-b border-[#DDDDDD] gap-3.5">
              <span className="font-bold min-w-[110px] text-right">Discount:</span>
              <span className="min-w-[120px] text-right font-medium">-{fmtWestern(calculations.discountTotal)}</span>
            </div>
          )}

          {taxRatePresets.some((t) => t.type === 'cgst_sgst') && (
            <div className="flex justify-end items-center py-1 gap-3.5 print:hidden">
              <span className="text-[#444] min-w-[110px] text-right">GST Preset:</span>
              <select
                aria-label="Apply GST rate preset"
                value={selectedTaxPresetId}
                onChange={(e) => applyTaxPreset(e.target.value)}
                className="text-[12px] border border-gray-300 rounded px-2 py-0.5 bg-white"
              >
                <option value="">Manual</option>
                {taxRatePresets.filter((t) => t.type === 'cgst_sgst').map((t) => (
                  <option key={t._id} value={t._id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex justify-end items-center py-1 border-b border-[#DDDDDD] gap-3.5">
            <span className="text-[#444] min-w-[110px] text-right">CGST Rate (%):</span>
            <input
              type="number"
              step="any"
              value={cgstRate}
              onChange={(e) => setCgstRate(parseFloat(e.target.value) || 0)}
              className="w-14 text-right bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
            />
            <span className="flex-1"></span>
            <span className="font-bold min-w-[110px] text-right">CGST:</span>
            <span className="min-w-[120px] text-right font-medium">{fmtWestern(calculations.cgst)}</span>
          </div>

          <div className="flex justify-end items-center py-1 border-b border-[#DDDDDD] gap-3.5">
            <span className="text-[#444] min-w-[110px] text-right">SGST Rate (%):</span>
            <input
              type="number"
              step="any"
              value={sgstRate}
              onChange={(e) => setSgstRate(parseFloat(e.target.value) || 0)}
              className="w-14 text-right bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
            />
            <span className="flex-1"></span>
            <span className="font-bold min-w-[110px] text-right">SGST:</span>
            <span className="min-w-[120px] text-right font-medium">{fmtWestern(calculations.sgst)}</span>
          </div>

          <div className="flex justify-end items-center py-1 border-b border-[#DDDDDD] gap-3.5">
            <span className="font-bold min-w-[110px] text-right">Total Tax:</span>
            <span className="min-w-[120px] text-right font-medium">{fmtWestern(calculations.tax)}</span>
          </div>

          <div className="flex justify-between items-center py-1.5 pr-2.5">
            <span>
              <b>Due Date:</b>{' '}
              <input
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0 w-32 ml-1"
              />
            </span>
          </div>

          <div className="flex justify-end items-center bg-[#D2D6DE] text-[14px] font-bold p-[8px_10px] mt-0.5 gap-3.5">
            <span className="min-w-[110px] text-right">Grand Total:</span>
            <span className="min-w-[120px] text-right font-black">{fmtWestern(calculations.grandTotal)}</span>
          </div>

          <div className="flex gap-2 mt-1.5 text-[12px]">
            <span className="font-bold whitespace-nowrap">Total in Words:</span>
            <span>{wordsManual || calculations.autoWords}</span>
            <span className="text-[#888] text-[11px] print:hidden">(edit if needed:</span>
            <input
              value={wordsManual}
              onChange={(e) => setWordsManual(e.target.value)}
              placeholder={calculations.autoWords}
              className="flex-1 bg-transparent border-b border-gray-200 focus:border-[#34495E] focus:outline-none text-[11.5px] print:hidden"
            />
            <span className="text-[#888] text-[11px] print:hidden">)</span>
          </div>
        </div>

        {/* ── TERMS & CONDITIONS ────────────────────────────────────────────── */}
        <div className="bg-[#D2D6DE] text-center font-bold py-1.5 mt-[22px] text-[13px]">
          Terms &amp; Condition's
        </div>
        <ul className="m-0 p-0 list-none">
          {terms.map((t, idx) => (
            <li key={idx} className="py-1 px-1 border-b border-[#DDDDDD] text-[12px] flex items-center justify-between">
              <input
                value={t}
                onChange={(e) => updateTerm(idx, e.target.value)}
                className="w-full bg-transparent rounded border border-gray-300 hover:border-gray-400 focus:border-[#34495E] focus:outline-none focus:ring-1 focus:ring-[#34495E]/20 px-1.5 py-0.5 print:rounded-none print:border-transparent print:ring-0 print:px-0 print:py-0"
              />
              <button
                onClick={() => {
                  termsTouchedRef.current = true;
                  setTerms((prev) => prev.filter((_, i) => i !== idx));
                }}
                className="print:hidden text-danger-text hover:text-danger-text text-xs ml-2"
              >
                &times;
              </button>
            </li>
          ))}
        </ul>
        <div className="py-1 flex items-center gap-2 print:hidden">
          <button
            onClick={addTerm}
            className="bg-[#f2f8fd] border border-dashed border-[#9cc4e0] text-[#0069a3] text-[11.5px] rounded px-2.5 py-1 hover:bg-info/10"
          >
            + Add term
          </button>
          <select
            aria-label="Add term from library"
            value={termLibrarySelection}
            onChange={(e) => e.target.value && addTermFromLibrary(e.target.value)}
            className="text-[11.5px] border border-[#9cc4e0] rounded px-2 py-1 text-[#0069a3] bg-white"
          >
            <option value="">+ Add from Terms Library…</option>
            {effectiveTerms.map((t) => (
              <option key={t._id} value={t._id}>{t.title}</option>
            ))}
          </select>
        </div>

        {/* ── PAYMENT & BANK DETAILS ────────────────────────────────────────── */}
        <div className="font-bold mt-4 mb-1.5 text-[13px]">For Payment:</div>
        <div className="grid grid-cols-3 gap-[6px_10px] text-[12px] mb-1.5">
          <div>
            <span className="font-bold mr-1">UPI ID:</span>
            <span>yespay.bizsbiz236943@yesbankltd</span>
          </div>
          <div>
            <span className="font-bold mr-1">Bank:</span>
            <span>YES BANK LTD.</span>
          </div>
          <div>
            <span className="font-bold mr-1">Branch:</span>
            <span>Wakad</span>
          </div>
          <div>
            <span className="font-bold mr-1">A/c No.:</span>
            <span>072884600000200</span>
          </div>
          <div>
            <span className="font-bold mr-1">IFSC:</span>
            <span>YESB0000728</span>
          </div>
          <div></div>
        </div>
        <div className="text-[12px] mt-1">
          <span className="font-bold mr-1">For International Payments (SWIFT):</span>
          <span>YESBINBB</span>
        </div>

        {/* ── FOOTER ───────────────────────────────────────────────────────── */}
        <div className="flex justify-between border-t-2 border-[#ccc] mt-5 pt-2.5 text-[11px] text-[#333]">
          <div>For Industrial &amp; Machine Use Only.</div>
          <div className="text-[#34495E]">Website: www.rbmoilcorporation.com/in</div>
        </div>
      </div>
    </div>
  );
}
